<script setup>
import { useHead } from '@unhead/vue'
import { useTranslation } from 'i18next-vue'
import throttle from 'lodash/throttle.js'
import { computed, watch, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'

import LeftNav from '@components/LeftNav.vue'
import NewsletterBanner from '@components/NewsletterBanner.vue'
import Footer from '@components/Footer.vue'
import { cleanUpString } from '@helpers/sanitize.js'

const store = useStore()
const { t, i18next } = useTranslation()

const metaDescription = computed(() => cleanUpString(t('meta.description')))
const metaImage = computed(() => t('meta.image'))

useHead({
  titleTemplate: computed(() => cleanUpString(t('meta.titleTemplate'))),
  meta: [
    {
      property: 'og:image',
      content: metaImage
    },
    {
      property: 'og:description',
      content: metaDescription
    },
    {
      name: 'twitter:card',
      content: metaDescription
    }
  ]
})

const locale = computed(() => store.getters['Global/locale'])
const leftNavOpened = computed(() => store.getters['Global/leftNavOpened'])
const leftNavActivated = computed(() => store.getters['Global/leftNavActivated'])

const updateViewportFromWindow = () => store.dispatch('Global/updateViewportFromWindow')
const updateLeftNavActivated = (value) => store.dispatch('Global/updateLeftNavActivated', value)
const setLanguageInitial = () => store.dispatch('FilterBar/setLanguageInitial', locale.value)

setLanguageInitial()

const updateLocale = () => {
  i18next.changeLanguage(locale.value)
}

const onResize = throttle(() => {
  store.dispatch('Global/updateViewportFromWindow')
  updateViewportFromWindow()
  updateLeftNavActivated(false)
}, 200)

const showNewsletterBanner = () => {
  return ['film', 'series', 'seriesIdFallback', 'interactive', 'interactiveIdFallback'].indexOf(this.$route.meta.rawName) !== -1
}

watch(locale, () => updateLocale(), { immediate: true })

onMounted(() => {
  window.addEventListener('resize', onResize)
  store.dispatch('Global/updateViewportFromWindow', true)
})

onUnmounted(() => {
  window.removeEventListener('resize', onResize)
})

</script>
<template>
  <div
    id="l-generalContainer"
    :class="[{'is-activated': leftNavActivated, 'is-nav-open': leftNavOpened }, 'is-nav-close']"
  >
    <LeftNav />
    <div id="l-rightContentSide">
      <main
        id="l-mainPageContent"
        class="is-loaded"
      >
        <router-view />
      </main>
      <div class="footers-container">
        <NewsletterBanner
          v-if="showNewsletterBanner"
          data-cy="newsletter-banner"
        />
        <Footer />
      </div>
    </div>
  </div>
</template>
